/**
 * HISI-006: Holographic Card Component
 * Glassmorphic cards with depth and glow effects
 */

import React, { ReactNode } from 'react';

interface HolographicCardProps {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  glow?: 'cyan' | 'blue' | 'purple' | 'success' | 'warning' | 'error';
  intensity?: 'subtle' | 'medium' | 'intense';
}

const HolographicCard: React.FC<HolographicCardProps> = ({
  children,
  className = '',
  onClick,
  glow = 'cyan',
  intensity = 'subtle'
}) => {
  const glowColors = {
    cyan: 'rgba(6, 182, 212',
    blue: 'rgba(59, 130, 246',
    purple: 'rgba(168, 85, 247',
    success: 'rgba(16, 185, 129',
    warning: 'rgba(245, 158, 11',
    error: 'rgba(239, 68, 68'
  };

  const glowIntensity = {
    subtle: '0.2)',
    medium: '0.4)',
    intense: '0.6)'
  };

  const glowStyle = {
    '--glow-color': `${glowColors[glow]}, ${glowIntensity[intensity]}`,
    '--glow-color-hover': `${glowColors[glow]}, ${intensity === 'subtle' ? '0.4)' : intensity === 'medium' ? '0.6)' : '0.8)'}`
  } as React.CSSProperties;

  return (
    <div 
      className={`holographic-card ${className}`}
      onClick={onClick}
      style={glowStyle}
    >
      {children}
    </div>
  );
};

export const CardHeader: React.FC<{
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  className?: string;
  showScanLine?: boolean;
  actions?: React.ReactNode;
  subtitleClassName?: string;
  onClick?: () => void;
}> = ({ title, subtitle, icon, className = '', showScanLine = false, actions, subtitleClassName, onClick }) => (
  <div className={`card-header-future ${showScanLine ? 'activity-active' : ''} ${className}`} onClick={onClick}>
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <h3 className="text-lg font-light text-gradient-cyan tracking-wide">
          {title}
        </h3>
        {subtitle && (
          <p className={`text-sm mt-1 ${subtitleClassName ?? 'opacity-70'}`}>{subtitle}</p>
        )}
      </div>
      <div className="flex items-center gap-3">
        {icon && (
          <div className="w-10 h-10 flex items-center justify-center opacity-50">
            {icon}
          </div>
        )}
        {actions}
      </div>
    </div>
  </div>
);

export const CardContent: React.FC<{
  children: ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <div className={`p-6 ${className}`}>
    {children}
  </div>
);

export default HolographicCard;